import { getKnowledgeTextExtractionWarningText } from "@frontend-shared/utils";
import { FileWarning } from "lucide-react";
import React, { memo } from "react";
import { useTranslation } from "react-i18next";

import { HoverTapTooltip } from "@/components/HoverTapTooltip";
import { Badge } from "@/components/ui/badge";

import type { IKnowledgeTextExtractionWarningChipProps } from "./types";

// NOTE: NSAI-7345 — a PDF that yielded too little text for its page count still
// completes, searchable for what was read. Its row carries a "Little text" chip
// beside the status badge, and the chip says how little on hover, keyboard
// focus or tap, so the list stays one line a file while the owner still learns
// that scanned pages or text inside images were not indexed.
const KnowledgeTextExtractionWarningChipInner: React.FC<
  IKnowledgeTextExtractionWarningChipProps
> = ({ warning, testId }) => {
  // NOTE: The instance's `t` for the sentence — the warning keys carry their
  // namespace ("knowledge:…"), which the hook's namespace-bound `t` does not
  // accept.
  const { t, i18n } = useTranslation("knowledge");
  const text = getKnowledgeTextExtractionWarningText({
    warning,
    language: i18n.language,
  });

  if (!text) {
    return null;
  }

  const label = t("textExtractionWarnings.chip");

  return (
    <HoverTapTooltip
      label={label}
      content={i18n.t(text.key, text.values)}
      testId={testId}
    >
      <Badge
        variant="outline"
        className="cursor-help gap-1 whitespace-nowrap border-warning/60 bg-warning/20 text-warning-foreground hover:bg-warning/30"
      >
        <FileWarning className="size-3 shrink-0" aria-hidden="true" />
        {label}
      </Badge>
    </HoverTapTooltip>
  );
};

export const KnowledgeTextExtractionWarningChip = memo(
  KnowledgeTextExtractionWarningChipInner,
);
